// src/components/RichTextEditor.tsx
import React, { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Quote, Image, Table, Eye, Save, X, Maximize2, Minimize2, Plus, Trash2, ChevronLeft, ChevronRight, Type, Palette, Link } from 'lucide-react';
import { CustomTable } from './CustomTable';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  showPreview?: boolean;
  onPreview?: () => void;
  onSave?: () => void;
  onClose?: () => void;
  onInsertTable?: () => void;
  title?: string;
  onTitleChange?: (title: string) => void;
  excerpt?: string;
  onExcerptChange?: (excerpt: string) => void;
  author?: string;
  onAuthorChange?: (author: string) => void;
  category?: string;
  onCategoryChange?: (category: string) => void;
  publishDate?: string;
  onPublishDateChange?: (date: string) => void;
  readTime?: string;
  onReadTimeChange?: (time: string) => void;
  featured?: boolean;
  onFeaturedChange?: (featured: boolean) => void;
  submitting?: boolean;
  initialImages?: string[];
  onImagesChange?: (images: string[]) => void;
  categories?: string[]; // Add categories prop
}

// Simple Text Editor Component
const SimpleTextEditor = React.forwardRef<HTMLDivElement, {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
}>(({ value, onChange, placeholder, height }, ref) => {
  const [isComposing, setIsComposing] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const handleInput = useCallback(() => {
    if (editorRef.current && !isComposing) {
      const content = editorRef.current.innerHTML || '';
      onChange(content);
    }
  }, [onChange, isComposing]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Handle list behavior
    if (e.key === 'Enter' || e.key === 'Backspace') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const currentNode = range.startContainer;
        
        // Check if we're in a list item
        let listItem = currentNode as HTMLElement;
        if (listItem.nodeType === Node.TEXT_NODE) {
          listItem = listItem.parentElement as HTMLElement;
        }
        
        if (listItem && listItem.tagName === 'LI') {
          const listElement = listItem.parentNode as HTMLElement;
          const listType = listElement.tagName; // UL or OL
          
          if (e.key === 'Enter') {
            // Check if the current list item is empty
            const isEmpty = listItem.textContent === '' || listItem.textContent === '\n';
            
            if (isEmpty) {
              // Exit the list if pressing Enter on an empty list item
              e.preventDefault();
              
              // Create a paragraph after the list
              const p = document.createElement('p');
              p.innerHTML = '<br>';
              
              // Insert the paragraph after the list
              if (listElement.parentNode) {
                listElement.parentNode.insertBefore(p, listElement.nextSibling);
              }
              
              // Remove the empty list item
              listItem.remove();
              
              // If the list is now empty, remove it
              if (listElement.children.length === 0) {
                listElement.remove();
              }
              
              // Move cursor to the new paragraph
              const newRange = document.createRange();
              newRange.setStart(p, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
              
              onChange(editorRef.current?.innerHTML || '');
              return;
            } else if (e.shiftKey) {
              // Exit list with Shift+Enter
              e.preventDefault();
              
              // Create a paragraph after the list
              const p = document.createElement('p');
              p.innerHTML = '<br>';
              
              // Insert the paragraph after the list
              if (listElement.parentNode) {
                listElement.parentNode.insertBefore(p, listElement.nextSibling);
              }
              
              // Move cursor to the new paragraph
              const newRange = document.createRange();
              newRange.setStart(p, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
              
              onChange(editorRef.current?.innerHTML || '');
              return;
            } else {
              // Continue list with Enter
              e.preventDefault();
              
              // Create new list item
              const newLi = document.createElement('li');
              newLi.innerHTML = '<br>';
              
              // Insert after current list item
              if (listItem.nextSibling) {
                listElement.insertBefore(newLi, listItem.nextSibling);
              } else {
                listElement.appendChild(newLi);
              }
              
              // Move cursor to new list item
              const newRange = document.createRange();
              newRange.setStart(newLi, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
              
              onChange(editorRef.current?.innerHTML || '');
              return;
            }
          } else if (e.key === 'Backspace') {
            // Check if we're at the beginning of the list item
            const isAtStart = range.startOffset === 0;
            
            if (isAtStart) {
              // Check if this is the first list item
              const isFirstItem = !listItem.previousSibling;
              
              if (isFirstItem) {
                // Exit the list if pressing Backspace at the beginning of the first list item
                e.preventDefault();
                
                // Create a paragraph before the list
                const p = document.createElement('p');
                p.innerHTML = listItem.innerHTML || '<br>';
                
                // Insert the paragraph before the list
                if (listElement.parentNode) {
                  listElement.parentNode.insertBefore(p, listElement);
                }
                
                // Remove the list item
                listItem.remove();
                
                // If the list is now empty, remove it
                if (listElement.children.length === 0) {
                  listElement.remove();
                }
                
                // Move cursor to the new paragraph
                const newRange = document.createRange();
                newRange.setStart(p, 0);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                
                onChange(editorRef.current?.innerHTML || '');
                return;
              }
            }
          }
        }
      }
    }
    
    // Default behavior for Enter if not in a list
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '<br><br>');
    }
  }, [onChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    
    // Get clipboard data
    const htmlData = e.clipboardData.getData('text/html');
    const textData = e.clipboardData.getData('text/plain');
    
    if (htmlData) {
      // Try to paste with formatting
      document.execCommand('insertHTML', false, htmlData);
    } else if (textData) {
      // Fallback to plain text
      document.execCommand('insertText', false, textData);
    }
    
    // Update content after paste
    setTimeout(() => {
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML || '');
      }
    }, 10);
  }, [onChange]);

  // Update content when value prop changes
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Save cursor position
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const startOffset = range?.startOffset;
      const endOffset = range?.endOffset;
      
      // Update content
      editorRef.current.innerHTML = value;
      
      // Restore cursor position
      if (range && selection) {
        try {
          const newRange = document.createRange();
          const textNode = editorRef.current?.firstChild;
          if (textNode) {
            newRange.setStart(textNode, Math.min(startOffset || 0, textNode.length));
            newRange.setEnd(textNode, Math.min(endOffset || 0, textNode.length));
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        } catch (e) {
          // Ignore cursor restoration errors
        }
      }
    }
  }, [value]);

  return (
    <div
      ref={(node) => {
        editorRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      contentEditable
      className="prose prose-sm max-w-none focus:outline-none p-4 min-h-[400px] overflow-y-auto"
      style={{ height }}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={() => setIsComposing(false)}
      suppressContentEditableWarning={true}
      data-placeholder={placeholder}
    />
  );
});

SimpleTextEditor.displayName = 'SimpleTextEditor';

export const RichTextEditor = forwardRef<any, RichTextEditorProps>(({
  value,
  onChange,
  placeholder = "Commencez à rédiger votre article...",
  height = "400px",
  showPreview = false,
  onPreview,
  onSave,
  onClose,
  onInsertTable,
  title,
  onTitleChange,
  excerpt,
  onExcerptChange,
  author,
  onAuthorChange,
  category,
  onCategoryChange,
  publishDate,
  onPublishDateChange,
  readTime,
  onReadTimeChange,
  featured,
  onFeaturedChange,
  submitting = false,
  initialImages = [],
  onImagesChange,
  categories = ['Actualités', 'Réglementation', 'Marché', 'Conseils', 'Analyse'] // Default categories
}, ref) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tables, setTables] = useState<{id: string, html: string, data?: string[][]}[]>([]);
  
  // Header images (Images de l'article) - separate from editor images
  const [headerImages, setHeaderImages] = useState<string[]>(initialImages);
  const [currentHeaderImageIndex, setCurrentHeaderImageIndex] = useState(0);
  const [uploadingHeaderImages, setUploadingHeaderImages] = useState(false);
  
  // Editor images (for insertion into text)
  const [editorImages, setEditorImages] = useState<string[]>([]);
  const [uploadingEditorImages, setUploadingEditorImages] = useState(false);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [selectedImageForInsert, setSelectedImageForInsert] = useState<string | null>(null);
  const [imageWidth, setImageWidth] = useState('100%');
  
  // Video settings
  const [showVideoSettings, setShowVideoSettings] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoWidth, setVideoWidth] = useState('560');
  const [videoHeight, setVideoHeight] = useState('315');
  
  // Table builder
  const [showTableBuilder, setShowTableBuilder] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [tableData, setTableData] = useState<string[][]>(
    Array(3).fill(null).map(() => Array(3).fill(''))
  );
  
  // Heading selector
  const [selectedHeading, setSelectedHeading] = useState('p');
  
  // Color pickers
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showUnderlineColorPicker, setShowUnderlineColorPicker] = useState(false);
  const [selectedTextColor, setSelectedTextColor] = useState('#000000');
  const [selectedUnderlineColor, setSelectedUnderlineColor] = useState('#000000');
  
  // Link settings
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkTarget, setLinkTarget] = useState('_blank'); // Default to opening in new tab
  
  const [editorError, setEditorError] = useState(false);
  const headerFileInputRef = useRef<HTMLInputElement>(null);
  const editorFileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  
  // Cursor position storage
  const savedRangeRef = useRef<Range | null>(null);
  const savedSelectionInfo = useRef<{ container: Node; offset: number } | null>(null);

  // Color palette
  const colorPalette = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#FFC0CB', '#A52A2A',
    '#808080', '#C0C0C0', '#FFD700', '#4B0082', '#008080', '#F0E68C',
    '#D4452C', '#b83a24', '#374151', '#6b7280', '#f3f4f6', '#1f2937'
  ];

  // Ensure we're on the client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    return url;
  };

  // Save cursor position with more robust method
  const saveCursorPosition = useCallback(() => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      // Store the range
      savedRangeRef.current = range.cloneRange();
      
      // Also store selection info as fallback
      const container = range.startContainer;
      const offset = range.startOffset;
      savedSelectionInfo.current = { container, offset };
      
      // Store a marker element in the editor
      const marker = document.createElement('span');
      marker.id = 'cursor-position-marker';
      marker.style.display = 'none';
      marker.textContent = '';
      
      try {
        range.insertNode(marker);
      } catch (e) {
        // If we can't insert the marker, just rely on the range
      }
    }
  }, []);

  // Restore cursor position with more robust method
  const restoreCursorPosition = useCallback(() => {
    if (!editorRef.current) return;
    
    // First try to find the marker
    const marker = editorRef.current.querySelector('#cursor-position-marker');
    if (marker) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.setStartBefore(marker);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Remove the marker
        marker.remove();
        return;
      }
    }
    
    // Fallback to saved range
    if (savedRangeRef.current) {
      const selection = window.getSelection();
      if (selection) {
        try {
          selection.removeAllRanges();
          selection.addRange(savedRangeRef.current);
          editorRef.current.focus();
          return;
        } catch (e) {
          // Range is no longer valid
        }
      }
    }
    
    // Fallback to saved selection info
    if (savedSelectionInfo.current) {
      const selection = window.getSelection();
      if (selection) {
        try {
          const range = document.createRange();
          range.setStart(savedSelectionInfo.current.container, savedSelectionInfo.current.offset);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          editorRef.current.focus();
          return;
        } catch (e) {
          // Selection info is no longer valid
        }
      }
    }
    
    // Last resort: just focus the editor
    editorRef.current.focus();
  }, []);

  // Apply formatting
  const applyFormat = useCallback((command: string, value?: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      
      // Special handling for lists and blockquote
      if (command === 'insertUnorderedList' || command === 'insertOrderedList' || command === 'formatBlock') {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const selectedText = range.toString() || 'List item';
          
          if (command === 'formatBlock' && value === 'blockquote') {
            const blockquote = document.createElement('blockquote');
            blockquote.style.borderLeft = '4px solid #D4452C';
            blockquote.style.margin = '1em 0';
            blockquote.style.paddingLeft = '1em';
            blockquote.style.color = '#6b7280';
            blockquote.style.fontStyle = 'italic';
            blockquote.style.backgroundColor = '#f9fafb';
            blockquote.style.padding = '1em';
            blockquote.style.borderRadius = '0 0.5rem 0.5rem 0';
            blockquote.textContent = selectedText;
            
            range.deleteContents();
            range.insertNode(blockquote);
          } else if (command === 'insertUnorderedList') {
            // Check if we're already in a list
            const currentNode = range.startContainer;
            let listElement = currentNode as HTMLElement;
            if (listElement.nodeType === Node.TEXT_NODE) {
              listElement = listElement.parentElement as HTMLElement;
            }
            
            if (listElement && listElement.tagName === 'UL') {
              // Add new list item to existing list
              const li = document.createElement('li');
              li.innerHTML = '<br>';
              listElement.appendChild(li);
              
              // Move cursor to new list item
              const newRange = document.createRange();
              newRange.setStart(li, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              // Create new list
              const ul = document.createElement('ul');
              ul.style.marginLeft = '1.5em';
              const li = document.createElement('li');
              li.textContent = selectedText || 'List item';
              ul.appendChild(li);
              
              range.deleteContents();
              range.insertNode(ul);
              
              // Move cursor to list item
              const newRange = document.createRange();
              newRange.selectNodeContents(li);
              newRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          } else if (command === 'insertOrderedList') {
            // Check if we're already in a list
            const currentNode = range.startContainer;
            let listElement = currentNode as HTMLElement;
            if (listElement.nodeType === Node.TEXT_NODE) {
              listElement = listElement.parentElement as HTMLElement;
            }
            
            if (listElement && listElement.tagName === 'OL') {
              // Add new list item to existing list
              const li = document.createElement('li');
              li.innerHTML = '<br>';
              listElement.appendChild(li);
              
              // Move cursor to new list item
              const newRange = document.createRange();
              newRange.setStart(li, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } else {
              // Create new list
              const ol = document.createElement('ol');
              ol.style.marginLeft = '1.5em';
              const li = document.createElement('li');
              li.textContent = selectedText || 'List item';
              ol.appendChild(li);
              
              range.deleteContents();
              range.insertNode(ol);
              
              // Move cursor to list item
              const newRange = document.createRange();
              newRange.selectNodeContents(li);
              newRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          }
        }
      } else {
        document.execCommand(command, false, value);
      }
      
      onChange(editorRef.current.innerHTML || '');
    }
  }, [onChange]);

  // Apply text color
  const applyTextColor = useCallback((color: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('foreColor', false, color);
      onChange(editorRef.current.innerHTML || '');
      setShowTextColorPicker(false);
    }
  }, [onChange]);

  // Apply underline color
  const applyUnderlineColor = useCallback((color: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString();
        
        if (selectedText) {
          const span = document.createElement('span');
          span.style.textDecoration = 'underline';
          span.style.textDecorationColor = color;
          span.style.textDecorationThickness = '2px';
          
          range.deleteContents();
          range.insertNode(span);
          
          // Move cursor after the span
          range.setStartAfter(span);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      onChange(editorRef.current.innerHTML || '');
      setShowUnderlineColorPicker(false);
    }
  }, [onChange]);

  // Apply heading
  const applyHeading = useCallback((tag: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString() || 'Texte';
        
        const element = document.createElement(tag);
        element.textContent = selectedText;
        
        range.deleteContents();
        range.insertNode(element);
        
        // Move cursor after the element
        range.setStartAfter(element);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        onChange(editorRef.current.innerHTML || '');
      }
    }
  }, [onChange]);

  // Function to insert a link
  const insertLink = useCallback(() => {
    if (!editorRef.current) return;
    
    try {
      // Restore cursor position
      restoreCursorPosition();
      
      // Get selection
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString() || linkText;
        
        if (!linkUrl) {
          setEditorError(true);
          setTimeout(() => setEditorError(false), 3000);
          return;
        }
        
        // Create the link element
        const linkElement = document.createElement('a');
        linkElement.href = linkUrl;
        linkElement.textContent = selectedText || linkText;
        linkElement.target = linkTarget;
        linkElement.rel = 'noopener noreferrer';
        linkElement.style.color = '#D4452C';
        linkElement.style.textDecoration = 'underline';
        
        // Insert the link
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Move cursor after the link
        range.setStartAfter(linkElement);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Update content
      onChange(editorRef.current.innerHTML || '');
      
      // Close the dialog
      setShowLinkDialog(false);
      setLinkUrl('');
      setLinkText('');
      setLinkTarget('_blank');
    } catch (error) {
      console.error('Error inserting link:', error);
      setEditorError(true);
      setTimeout(() => setEditorError(false), 3000);
    }
  }, [linkUrl, linkText, linkTarget, onChange, restoreCursorPosition]);

  // Function to open link dialog
  const openLinkDialog = useCallback(() => {
    // Save cursor position before opening the dialog
    saveCursorPosition();
    
    // Get selected text to pre-fill the link text field
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const selectedText = selection.toString();
      setLinkText(selectedText);
    }
    
    setShowLinkDialog(true);
  }, [saveCursorPosition]);

  // Handle video insertion
  const insertVideo = useCallback(() => {
    if (!videoUrl || !editorRef.current) return;
    
    try {
      const embedUrl = getYouTubeEmbedUrl(videoUrl);
      
      // Restore cursor position
      restoreCursorPosition();
      
      // Get selection
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create a div for the video
        const videoContainer = document.createElement('div');
        videoContainer.style.margin = '10px 0';
        videoContainer.style.textAlign = 'center';
        
        // Create iframe
        const iframe = document.createElement('iframe');
        iframe.src = embedUrl;
        iframe.width = videoWidth;
        iframe.height = videoHeight;
        iframe.frameBorder = '0';
        iframe.setAttribute('allowfullscreen', '');
        iframe.style.maxWidth = '100%';
        iframe.style.borderRadius = '4px';
        
        videoContainer.appendChild(iframe);
        
        // Insert at cursor position
        range.deleteContents();
        range.insertNode(videoContainer);
        
        // Move cursor after the video
        range.setStartAfter(videoContainer);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append to the end
        const videoHtml = `<div style="margin: 10px 0; text-align: center;"><iframe src="${embedUrl}" width="${videoWidth}" height="${videoHeight}" frameborder="0" allowfullscreen style="max-width: 100%; border-radius: 4px;"></iframe></div>`;
        editorRef.current.innerHTML += videoHtml;
      }
      
      // Update content
      onChange(editorRef.current.innerHTML || '');
      
      // Close the settings modal
      setShowVideoSettings(false);
      setVideoUrl('');
    } catch (error) {
      console.error('Error inserting video:', error);
      setEditorError(true);
      setTimeout(() => setEditorError(false), 3000);
    }
  }, [videoUrl, videoWidth, videoHeight, onChange, restoreCursorPosition]);

  // Handle image insertion into editor
  const insertImageToEditor = useCallback(() => {
    if (!selectedImageForInsert || !editorRef.current) return;
    
    try {
      // Restore cursor position
      restoreCursorPosition();
      
      // Get selection
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create a div for the image
        const imageContainer = document.createElement('div');
        imageContainer.style.margin = '10px 0';
        imageContainer.style.textAlign = 'center';
        
        // Create img element
        const img = document.createElement('img');
        img.src = selectedImageForInsert;
        img.alt = 'Article image';
        img.style.width = imageWidth;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';
        img.style.borderRadius = '4px';
        
        imageContainer.appendChild(img);
        
        // Insert at cursor position
        range.deleteContents();
        range.insertNode(imageContainer);
        
        // Move cursor after the image
        range.setStartAfter(imageContainer);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append to the end
        const imageHtml = `<div style="margin: 10px 0; text-align: center;"><img src="${selectedImageForInsert}" alt="Article image" style="width: ${imageWidth}; max-width: 100%; height: auto; display: block; border-radius: 4px;" /></div>`;
        editorRef.current.innerHTML += imageHtml;
      }
      
      // Update content
      onChange(editorRef.current.innerHTML || '');
      
      // Close the selector
      setShowImageSelector(false);
      setSelectedImageForInsert(null);
    } catch (error) {
      console.error('Error inserting image:', error);
      setEditorError(true);
      setTimeout(() => setEditorError(false), 3000);
    }
  }, [selectedImageForInsert, imageWidth, onChange, restoreCursorPosition]);

  // Function to open image selector
  const openImageSelector = useCallback(() => {
    // Save cursor position before opening the modal
    saveCursorPosition();
    setShowImageSelector(true);
  }, [saveCursorPosition]);

  // Function to open video settings
  const openVideoSettings = useCallback(() => {
    // Save cursor position before opening the modal
    saveCursorPosition();
    setShowVideoSettings(true);
  }, [saveCursorPosition]);

  // Function to open table builder
  const openTableBuilder = useCallback(() => {
    // Save cursor position before opening the modal
    saveCursorPosition();
    setShowTableBuilder(true);
  }, [saveCursorPosition]);

  // Handle table data change
  const handleTableDataChange = (rowIndex: number, colIndex: number, value: string) => {
    const newData = [...tableData];
    newData[rowIndex][colIndex] = value;
    setTableData(newData);
  };

  // Add row
  const addTableRow = () => {
    const newRow = Array(tableColumns).fill('');
    setTableData([...tableData, newRow]);
    setTableRows(tableRows + 1);
  };

  // Add column
  const addTableColumn = () => {
    const newData = tableData.map(row => [...row, '']);
    setTableData(newData);
    setTableColumns(tableColumns + 1);
  };

  // Remove row
  const removeTableRow = (index: number) => {
    if (tableRows <= 1) return;
    const newData = tableData.filter((_, i) => i !== index);
    setTableData(newData);
    setTableRows(tableRows - 1);
  };

  // Remove column
  const removeTableColumn = (index: number) => {
    if (tableColumns <= 1) return;
    const newData = tableData.map(row => row.filter((_, i) => i !== index));
    setTableData(newData);
    setTableColumns(tableColumns - 1);
  };

  // Insert table
  const insertTable = useCallback(() => {
    if (!editorRef.current) return;
    
    try {
      const tableId = `table-${Date.now()}`;
      
      // Restore cursor position
      restoreCursorPosition();
      
      // Get selection
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create a div for the table
        const tableContainer = document.createElement('div');
        tableContainer.className = 'custom-table-container';
        tableContainer.setAttribute('data-table-id', tableId);
        tableContainer.style.margin = '10px 0';
        
        // Create table
        const table = document.createElement('table');
        table.className = 'custom-table';
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.border = '1px solid #e5e7eb';
        
        // Generate table rows and cells
        for (let i = 0; i < tableRows; i++) {
          const tr = document.createElement('tr');
          
          for (let j = 0; j < tableColumns; j++) {
            const isHeader = i === 0;
            const cell = document.createElement(isHeader ? 'th' : 'td');
            cell.style.padding = '8px 12px';
            cell.style.border = '1px solid #e5e7eb';
            cell.style.textAlign = 'left';
            
            if (isHeader) {
              cell.style.backgroundColor = '#f9fafb';
              cell.style.fontWeight = 'bold';
            }
            
            cell.textContent = tableData[i][j] || (isHeader ? `Header ${j + 1}` : `Cell ${i}-${j}`);
            tr.appendChild(cell);
          }
          
          table.appendChild(tr);
        }
        
        tableContainer.appendChild(table);
        
        // Insert at cursor position
        range.deleteContents();
        range.insertNode(tableContainer);
        
        // Move cursor after the table
        range.setStartAfter(tableContainer);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append to the end
        let tableHtml = `<div class="custom-table-container" data-table-id="${tableId}" style="margin: 10px 0;">`;
        tableHtml += '<table class="custom-table" style="width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb;">';
        
        // Generate table HTML
        for (let i = 0; i < tableRows; i++) {
          tableHtml += '<tr>';
          for (let j = 0; j < tableColumns; j++) {
            const isHeader = i === 0;
            const tag = isHeader ? 'th' : 'td';
            const style = isHeader 
              ? 'padding: 8px 12px; border: 1px solid #e5e7eb; background-color: #f9fafb; font-weight: bold; text-align: left;'
              : 'padding: 8px 12px; border: 1px solid #e5e7eb; text-align: left;';
            tableHtml += `<${tag} style="${style}">${tableData[i][j] || (isHeader ? `Header ${j + 1}` : `Cell ${i}-${j}`)}</${tag}>`;
          }
          tableHtml += '</tr>';
        }
        
        tableHtml += '</table></div>';
        editorRef.current.innerHTML += tableHtml;
      }
      
      // Update content
      onChange(editorRef.current.innerHTML || '');
      
      // Add to our tables state
      setTables(prev => [...prev, { 
        id: tableId, 
        html: editorRef.current?.innerHTML || '', 
        data: tableData
      }]);
      
      // Close the builder
      setShowTableBuilder(false);
    } catch (error) {
      console.error('Error inserting table:', error);
      setEditorError(true);
      setTimeout(() => setEditorError(false), 3000);
    }
  }, [tableData, tableRows, tableColumns, onChange, restoreCursorPosition]);

  // Handle header images upload (Images de l'article)
  const handleHeaderImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    try {
      setUploadingHeaderImages(true);
      const newImages: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        
        await new Promise<void>((resolve) => {
          reader.onload = (e) => {
            const imageUrl = e.target?.result as string;
            newImages.push(imageUrl);
            resolve();
          };
          reader.readAsDataURL(file);
        });
      }
      
      setHeaderImages(prev => [...prev, ...newImages]);
      if (onImagesChange) {
        onImagesChange([...headerImages, ...newImages]);
      }
    } catch (error) {
      console.error('Error uploading header images:', error);
    } finally {
      setUploadingHeaderImages(false);
    }
  };

  // Handle editor images upload (for insertion into text)
  const handleEditorImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    try {
      setUploadingEditorImages(true);
      const newImages: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        
        await new Promise<void>((resolve) => {
          reader.onload = (e) => {
            const imageUrl = e.target?.result as string;
            newImages.push(imageUrl);
            resolve();
          };
          reader.readAsDataURL(file);
        });
      }
      
      setEditorImages(prev => [...prev, ...newImages]);
    } catch (error) {
      console.error('Error uploading editor images:', error);
    } finally {
      setUploadingEditorImages(false);
    }
  };

  // Handle header image deletion
  const handleDeleteHeaderImage = useCallback((index: number) => {
    const newImages = headerImages.filter((_, i) => i !== index);
    setHeaderImages(newImages);
    if (onImagesChange) {
      onImagesChange(newImages);
    }
    
    // Adjust current index if necessary
    if (currentHeaderImageIndex >= newImages.length && newImages.length > 0) {
      setCurrentHeaderImageIndex(newImages.length - 1);
    }
  }, [headerImages, currentHeaderImageIndex, onImagesChange]);

  // Handle editor image deletion
  const handleDeleteEditorImage = useCallback((index: number) => {
    const newImages = editorImages.filter((_, i) => i !== index);
    setEditorImages(newImages);
    
    // If the deleted image was selected, clear selection
    if (selectedImageForInsert === editorImages[index]) {
      setSelectedImageForInsert(null);
    }
  }, [editorImages, selectedImageForInsert]);

  // Header image navigation
  const nextHeaderImage = useCallback(() => {
    if (headerImages.length <= 1) return;
    setCurrentHeaderImageIndex((prev) => (prev + 1) % headerImages.length);
  }, [headerImages.length]);

  const prevHeaderImage = useCallback(() => {
    if (headerImages.length <= 1) return;
    setCurrentHeaderImageIndex((prev) => (prev - 1 + headerImages.length) % headerImages.length);
  }, [headerImages.length]);

  // Parse existing tables from the content
  useEffect(() => {
    if (value) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        const tableContainers = doc.querySelectorAll('.custom-table-container');
        
        const extractedTables = Array.from(tableContainers).map(container => {
          const tableId = container.getAttribute('data-table-id') || `table-${Date.now()}`;
          const html = container.outerHTML;
          
          // Parse table data
          const table = container.querySelector('table');
          let data: string[][] = [];
          if (table) {
            const rows = Array.from(table.querySelectorAll('tr'));
            data = rows.map(row => {
              const cells = Array.from(row.querySelectorAll('th, td'));
              return cells.map(cell => cell.textContent || '');
            });
          }
          
          return { id: tableId, html, data };
        });
        
        setTables(extractedTables);
      } catch (error) {
        console.error('Error parsing tables:', error);
      }
    }
  }, [value]);

  // Update header images when initialImages change
  useEffect(() => {
    if (initialImages.length > 0 && headerImages.length === 0) {
      setHeaderImages(initialImages);
    }
  }, [initialImages, headerImages.length]);

  // Add custom styles
  useEffect(() => {
    if (!isClient) return;
    
    const style = document.createElement('style');
    style.textContent = `
      .editor-container {
        font-size: 16px;
        line-height: 1.6;
        color: #374151;
      }
      
      .editor-container h1 { 
        font-size: 2em; 
        font-weight: bold; 
        margin: 0.5em 0; 
        color: #111827;
      }
      .editor-container h2 { 
        font-size: 1.5em; 
        font-weight: bold; 
        margin: 0.5em 0; 
        color: #111827;
      }
      .editor-container h3 { 
        font-size: 1.17em; 
        font-weight: bold; 
        margin: 0.5em 0; 
        color: #111827;
      }
      .editor-container h4 { 
        font-size: 1em; 
        font-weight: bold; 
        margin: 0.5em 0; 
        color: #111827;
      }
      .editor-container h5 { 
        font-size: 0.83em; 
        font-weight: bold; 
        margin: 0.5em 0; 
        color: #111827;
      }
      .editor-container h6 { 
        font-size: 0.67em; 
        font-weight: bold; 
        margin: 0.5em 0; 
        color: #111827;
      }
      
      .editor-container blockquote {
        border-left: 4px solid #D4452C;
        margin: 1em 0;
        padding-left: 1em;
        color: #6b7280;
        font-style: italic;
        background-color: #f9fafb;
        padding: 1em;
        border-radius: 0 0.5rem 0.5rem 0;
      }
      
      .editor-container ul {
        margin-left: 1.5em;
        list-style-type: disc;
      }
      
      .editor-container ol {
        margin-left: 1.5em;
        list-style-type: decimal;
      }
      
      .editor-container li {
        margin: 0.25em 0;
      }
      
      .editor-container li:empty::before {
        content: " ";
        white-space: pre;
      }
      
      .editor-container a {
        color: #D4452C;
        text-decoration: underline;
      }
      
      .editor-container a:hover {
        color: #b83a24;
      }
      
      .editor-container code {
        background-color: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-family: monospace;
        font-size: 0.875em;
      }
      
      .editor-container pre {
        background-color: #1f2937;
        color: #f9fafb;
        padding: 1rem;
        border-radius: 0.5rem;
        overflow-x: auto;
      }
      
      .custom-table-container {
        margin: 1em 0;
      }
      
      .custom-table {
        width: 100%;
        border-collapse: collapse;
      }
      
      .custom-table th,
      .custom-table td {
        padding: 8px 12px;
        text-align: left;
      }
      
      .custom-table th {
        background-color: #f9fafb;
        font-weight: bold;
      }
      
      .custom-table.bordered {
        border: 1px solid #e5e7eb;
      }
      
      .custom-table.bordered th,
      .custom-table.bordered td {
        border: 1px solid #e5e7eb;
      }
      
      .custom-table.striped tbody tr:nth-child(even) {
        background-color: #f9fafb';
      }
      
      .editor-image {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 10px 0;
        border-radius: 4px;
      }
      
      .editor-video {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 10px 0;
        border-radius: 4px;
      }
      
      [contenteditable]:empty:before {
        content: attr(data-placeholder);
        color: #9ca3af;
        pointer-events: none;
      }
      
      .color-picker {
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 50;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 0.375rem;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        padding: 0.5rem;
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: 0.25rem;
      }
      
      .color-picker button {
        width: 28px;
        height: 28px;
        border: 1px solid #e5e7eb;
        border-radius: 0.25rem;
        cursor: pointer;
        transition: all 0.2s;
      }
      
      .color-picker button:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }
      
      .color-picker button.active {
        border: 2px solid #3b82f6;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [isClient]);

  const togglePreview = useCallback(() => {
    setIsPreviewMode(!isPreviewMode);
    if (onPreview) {
      onPreview();
    }
  }, [isPreviewMode, onPreview]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
    }
  }, [onSave]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Render the content with custom tables
  const renderContent = useCallback(() => {
    if (!value) return '';
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(value, 'text/html');
      
      // Replace custom table placeholders with actual table components
      const tableContainers = doc.querySelectorAll('.custom-table-container');
      tableContainers.forEach(container => {
        const tableId = container.getAttribute('data-table-id');
        if (tableId) {
          const tableData = tables.find(t => t.id === tableId);
          if (tableData) {
            const placeholder = document.createElement('div');
            placeholder.setAttribute('data-table-placeholder', tableId);
            container.parentNode?.replaceChild(placeholder, container);
          }
        }
      });
      
      return doc.body.innerHTML;
    } catch (error) {
      console.error('Error rendering content:', error);
      return value;
    }
  }, [value, tables]);

  // Don't render until client-side
  if (!isClient) {
    return (
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Éditeur d'article</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-lg ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>
      <div className="border-b border-gray-200 p-4 bg-gray-50 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Éditeur d'article</h3>
          <div className="flex items-center space-x-2">
            {showPreview && (
              <button
                onClick={togglePreview}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isPreviewMode 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Eye className="h-4 w-4 inline mr-1" />
                {isPreviewMode ? 'Modifier' : 'Aperçu'}
              </button>
            )}
            {onSave && (
              <button
                onClick={handleSave}
                disabled={submitting}
                className="px-3 py-1.5 bg-[#D4452C] text-white rounded-md text-sm font-medium hover:bg-[#b83a24] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="mr-2 inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 inline mr-1" />
                    Enregistrer
                  </>
                )}
              </button>
            )}
            <button
              onClick={toggleFullscreen}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            {onClose && (
              <button
                onClick={handleClose}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={`p-4 ${isFullscreen ? 'max-w-6xl mx-auto' : ''}`}>
        {/* Title Input */}
        {onTitleChange && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de l'article *
            </label>
            <input
              type="text"
              value={title || ''}
              onChange={(e) => onTitleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
              placeholder="Entrez le titre de l'article..."
            />
          </div>
        )}

        {/* Excerpt Input */}
        {onExcerptChange && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extrait de l'article *
            </label>
            <textarea
              value={excerpt || ''}
              onChange={(e) => onExcerptChange(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent resize-none"
              placeholder="Rédigez un bref extrait de votre article..."
            />
          </div>
        )}


        {/* Editor Toolbar */}
        {!isPreviewMode && (
          <div className="mb-2">
            <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2">
              <div className="flex items-center space-x-1 flex-wrap">
                {/* Heading Selector */}
                <div className="relative">
                  <button
                    onClick={() => document.getElementById('heading-dropdown')?.classList.toggle('hidden')}
                    className="p-2 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                    title="Titre"
                  >
                    <Type className="h-4 w-4" />
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div id="heading-dropdown" className="hidden absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                    <button
                      onClick={() => { applyHeading('p'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Normal
                    </button>
                    <button
                      onClick={() => { applyHeading('h1'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Titre 1
                    </button>
                    <button
                      onClick={() => { applyHeading('h2'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Titre 2
                    </button>
                    <button
                      onClick={() => { applyHeading('h3'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Titre 3
                    </button>
                    <button
                      onClick={() => { applyHeading('h4'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Titre 4
                    </button>
                    <button
                      onClick={() => { applyHeading('h5'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Titre 5
                    </button>
                    <button
                      onClick={() => { applyHeading('h6'); document.getElementById('heading-dropdown')?.classList.add('hidden'); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      Titre 6
                    </button>
                  </div>
                </div>
                
                <div className="w-px h-6 bg-gray-300"></div>
                
                <button
                  onClick={() => applyFormat('bold')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Gras"
                >
                  <Bold className="h-4 w-4" />
                </button>
                <button
                  onClick={() => applyFormat('italic')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Italique"
                >
                  <Italic className="h-4 w-4" />
                </button>
                <button
                  onClick={() => applyFormat('underline')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Souligner"
                >
                  <Underline className="h-4 w-4" />
                </button>
                
                <div className="w-px h-6 bg-gray-300"></div>
                
                {/* Text Color Picker */}
                <div className="relative">
                  <button
                    onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                    className="p-2 rounded hover:bg-gray-200 transition-colors"
                    title="Couleur du texte"
                  >
                    <div className="w-4 h-4 border border-gray-400 rounded" style={{ backgroundColor: selectedTextColor }}></div>
                  </button>
                  {showTextColorPicker && (
                    <div className="color-picker">
                      {colorPalette.map((color) => (
                        <button
                          key={color}
                          onClick={() => applyTextColor(color)}
                          className={selectedTextColor === color ? 'active' : ''}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Underline Color Picker */}
                <div className="relative">
                  <button
                    onClick={() => setShowUnderlineColorPicker(!showUnderlineColorPicker)}
                    className="p-2 rounded hover:bg-gray-200 transition-colors"
                    title="Couleur du soulignement"
                  >
                    <Underline className="h-4 w-4" style={{ color: selectedUnderlineColor }} />
                  </button>
                  {showUnderlineColorPicker && (
                    <div className="color-picker">
                      {colorPalette.map((color) => (
                        <button
                          key={color}
                          onClick={() => applyUnderlineColor(color)}
                          className={selectedUnderlineColor === color ? 'active' : ''}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="w-px h-6 bg-gray-300"></div>
                
                <button
                  onClick={() => applyFormat('justifyLeft')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Aligner à gauche"
                >
                  <AlignLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => applyFormat('justifyCenter')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Centrer"
                >
                  <AlignCenter className="h-4 w-4" />
                </button>
                <button
                  onClick={() => applyFormat('justifyRight')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Aligner à droite"
                >
                  <AlignRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => applyFormat('justifyFull')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Justifier"
                >
                  <AlignJustify className="h-4 w-4" />
                </button>
                
                <div className="w-px h-6 bg-gray-300"></div>
                
                <button
                  onClick={() => applyFormat('insertUnorderedList')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Liste à puces"
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => applyFormat('insertOrderedList')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Liste numérotée"
                >
                  <ListOrdered className="h-4 w-4" />
                </button>
                
                <div className="w-px h-6 bg-gray-300"></div>
                
                <button
                  onClick={() => applyFormat('formatBlock', false, 'blockquote')}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Citation"
                >
                  <Quote className="h-4 w-4" />
                </button>
                
                <div className="w-px h-6 bg-gray-300"></div>
                
                {/* Link Button */}
                <button
                  onClick={openLinkDialog}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Insérer un lien"
                >
                  <Link className="h-4 w-4" />
                </button>
                
                <button
                  onClick={openImageSelector}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Insérer une image"
                >
                  <Image className="h-4 w-4" />
                </button>
                <button
                  onClick={openVideoSettings}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Insérer une vidéo"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={openTableBuilder}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Insérer un tableau"
                >
                  <Table className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Editor or Preview */}
        {!isPreviewMode ? (
          <div className={`border border-gray-300 rounded-b-lg overflow-hidden ${isFullscreen ? 'min-h-[500px]' : ''}`}>
            {editorError && (
              <div className="border border-red-300 bg-red-50 p-4 m-4 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <X className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Erreur dans l'éditeur
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>Une erreur s'est produite. Veuillez réessayer.</p>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setEditorError(false)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="editor-container">
              <SimpleTextEditor
                ref={editorRef}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                height={isFullscreen ? '500px' : height}
              />
            </div>
            
            {/* Render custom tables outside the editor */}
            {tables.map(table => (
              <CustomTable
                key={table.id}
                id={table.id}
                initialHtml={table.html}
                initialData={table.data}
                onSave={() => {}}
                onDelete={() => {}}
                onTableDataChange={() => {}}
              />
            ))}
          </div>
        ) : (
          <div className="prose prose prose-lg max-w-none">
            <div 
              className="min-h-[400px] p-6 border border-gray-200 rounded-lg bg-gray-50 overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: renderContent() }}
            />
            
            {/* Render custom tables in preview mode */}
            {tables.map(table => (
              <div key={table.id} dangerouslySetInnerHTML={{ __html: table.html }} />
            ))}
          </div>
        )}
      </div>

      {/* Metadata Section */}
      {(onAuthorChange || onCategoryChange || onPublishDateChange || onReadTimeChange || onFeaturedChange) && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {onAuthorChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auteur *
                </label>
                <input
                  type="text"
                  value={author || ''}
                  onChange={(e) => onAuthorChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                  placeholder="Nom de l'auteur"
                />
              </div>
            )}

            {onCategoryChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie *
                </label>
                <select
                  value={category || ''}
                  onChange={(e) => onCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}

            {onPublishDateChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de publication *
                </label>
                <input
                  type="date"
                  value={publishDate || ''}
                  onChange={(e) => onPublishDateChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                />
              </div>
            )}

            {onReadTimeChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Temps de lecture
                </label>
                <input
                  type="text"
                  value={readTime || ''}
                  onChange={(e) => onReadTimeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                  placeholder="ex: 5 min"
                />
              </div>
            )}

            {onFeaturedChange && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featured"
                  checked={featured || false}
                  onChange={(e) => onFeaturedChange(e.target.checked)}
                  className="h-4 w-4 text-[#D4452C] focus:ring-[#D4452C] border-gray-300 rounded"
                />
                <label htmlFor="featured" className="ml-2 text-sm text-gray-700">
                  Article en vedette
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Selector Modal for Editor Images */}
      {showImageSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Insérer une image dans l'article</h2>
              <button
                onClick={() => setShowImageSelector(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              {editorImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                  {editorImages.map((image, index) => (
                    <div 
                      key={index} 
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 ${
                        selectedImageForInsert === image ? 'border-blue-500' : 'border-gray-200'
                      }`}
                      onClick={() => setSelectedImageForInsert(image)}
                    >
                      <img 
                        src={image} 
                        alt={`Image ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      {selectedImageForInsert === image && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEditorImage(index);
                        }}
                        className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <Image className="h-full w-full" />
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Aucune image disponible. Veuillez d'abord télécharger des images.
                  </p>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Largeur de l'image
                </label>
                <select
                  value={imageWidth}
                  onChange={(e) => setImageWidth(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="25%">25%</option>
                  <option value="50%">50%</option>
                  <option value="75%">75%</option>
                  <option value="100%">100% (pleine largeur)</option>
                </select>
              </div>
              
              <div className="flex justify-between">
                <div>
                  <input
                    ref={editorFileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      handleEditorImageUpload(e.target.files);
                    }}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => editorFileInputRef.current?.click()}
                    disabled={uploadingEditorImages}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {uploadingEditorImages ? (
                      <>
                        <div className="mr-2 inline-block animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                        Téléchargement...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2 inline" />
                        Télécharger de nouvelles images
                      </>
                    )}
                  </button>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowImageSelector(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={insertImageToEditor}
                    disabled={!selectedImageForInsert}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Insérer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Dialog Modal */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Insérer un lien</h2>
              <button
                onClick={() => setShowLinkDialog(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du lien
                </label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texte du lien
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Texte à afficher"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cible du lien
                </label>
                <select
                  value={linkTarget}
                  onChange={(e) => setLinkTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="_blank">Ouvrir dans un nouvel onglet</option>
                  <option value="_self">Ouvrir dans le même onglet</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowLinkDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuler
                </button>
                <button
                  onClick={insertLink}
                  disabled={!linkUrl}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Insérer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Settings Modal */}
      {showVideoSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Insérer une vidéo</h2>
              <button
                onClick={() => setShowVideoSettings(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de la vidéo (YouTube)
                </label>
                <input
                  type="text"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Largeur (px)
                  </label>
                  <input
                    type="number"
                    value={videoWidth}
                    onChange={(e) => setVideoWidth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hauteur (px)
                  </label>
                  <input
                    type="number"
                    value={videoHeight}
                    onChange={(e) => setVideoHeight(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowVideoSettings(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuler
                </button>
                <button
                  onClick={insertVideo}
                  disabled={!videoUrl}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Insérer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Builder Modal */}
      {showTableBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Créer un tableau</h2>
              <button
                onClick={() => setShowTableBuilder(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              {/* Table Size Controls */}
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Lignes:</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={tableRows}
                    onChange={(e) => {
                      const newRows = parseInt(e.target.value) || 1;
                      setTableRows(newRows);
                      // Adjust data array
                      const newData = Array(newRows).fill(null).map((_, i) => 
                        i < tableData.length ? tableData[i] : Array(tableColumns).fill('')
                      );
                      setTableData(newData);
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Colonnes:</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={tableColumns}
                    onChange={(e) => {
                      const newCols = parseInt(e.target.value) || 1;
                      setTableColumns(newCols);
                      // Adjust data array
                      const newData = tableData.map(row => {
                        const newRow = [...row];
                        while (newRow.length < newCols) {
                          newRow.push('');
                        }
                        return newRow.slice(0, newCols);
                      });
                      setTableData(newData);
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
              
              {/* Table Editor */}
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-collapse border border-gray-300">
                  <tbody>
                    {tableData.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, colIndex) => (
                          <td key={colIndex} className="border border-gray-300 p-1">
                            <input
                              type="text"
                              value={cell}
                              onChange={(e) => handleTableDataChange(rowIndex, colIndex, e.target.value)}
                              className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              placeholder={rowIndex === 0 ? `Header ${colIndex + 1}` : `Cell ${rowIndex}-${colIndex}`}
                            />
                          </td>
                        ))}
                        <td className="border border-gray-300 p-1">
                          <button
                            onClick={() => removeTableRow(rowIndex)}
                            disabled={tableRows <= 1}
                            className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Supprimer la ligne"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      {Array(tableColumns).fill(null).map((_, colIndex) => (
                        <td key={colIndex} className="border border-gray-300 p-1">
                          <button
                            onClick={() => removeTableColumn(colIndex)}
                            disabled={tableColumns <= 1}
                            className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Supprimer la colonne"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      ))}
                      <td className="border border-gray-300"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={addTableRow}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                  >
                    <Plus className="h-4 w-4 inline mr-1" />
                    Ajouter une ligne
                  </button>
                  <button
                    onClick={addTableColumn}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                  >
                    <Plus className="h-4 w-4 inline mr-1" />
                    Ajouter une colonne
                  </button>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowTableBuilder(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={insertTable}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                    Insérer le tableau
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div> 
  );
});

RichTextEditor.displayName = 'RichTextEditor';